import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Grid,
  Card,
  Chip,
  Alert,
  Button,
  Paper,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Help as HelpIcon,
  Security as SecurityIcon,
  AccountBalance as BankIcon,
  Receipt as VATIcon,
  Business as BusinessIcon,
  Support as SupportIcon,
  Flag as UKFlagIcon
} from '@mui/icons-material';

const FAQPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState<string | false>(false);

  const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedFAQ(isExpanded ? panel : false);
  };

  const faqCategories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: <HelpIcon color="primary" />,
      faqs: [
        {
          id: 'what-is-executive-finance',
          question: 'What is EF-CapFlow?',
          answer: `EF-CapFlow is a modern financial management platform designed specifically for UK SMEs. 
          Unlike traditional solutions like Sage or Xero, we offer superior user experience, UK-specific compliance features, 
          and unique cross-domain business intelligence that connects with your CRM and supply chain systems.`
        },
        {
          id: 'how-different-from-sage',
          question: 'How is this different from Sage or Xero?',
          answer: `Key differences include: 1) Modern, intuitive interface that's actually enjoyable to use, 
          2) Built specifically for UK businesses with HMRC MTD compliance from day one, 3) Cross-domain integration 
          that provides unified business intelligence across finance, CRM, and supply chain, 4) Competitive pricing 
          starting at £19/month vs Sage's £37+/month, 5) UK-based support team that understands local requirements.`
        },
        {
          id: 'setup-time',
          question: 'How long does setup take?',
          answer: `Most businesses are up and running in under 30 minutes. Our guided onboarding process walks you 
          through company setup, bank connection via Open Banking, and initial configuration. You can start creating 
          invoices and managing finances immediately.`
        },
        {
          id: 'data-migration',
          question: 'Can I import data from my current system?',
          answer: `Yes! We support data import from major UK accounting systems including Sage, Xero, QuickBooks, 
          and CSV files. Our migration team can help transfer your customers, suppliers, chart of accounts, 
          and historical transactions at no extra cost.`
        }
      ]
    },
    {
      id: 'uk-compliance',
      title: 'UK Compliance & VAT',
      icon: <VATIcon color="primary" />,
      faqs: [
        {
          id: 'mtd-compliance',
          question: 'Is the system Making Tax Digital (MTD) compliant?',
          answer: `Yes, EF-CapFlow is fully MTD compliant and certified by HMRC. All transactions are 
          stored digitally with complete audit trails, and VAT returns are submitted directly to HMRC via their API. 
          You'll never need to worry about MTD compliance again.`
        },
        {
          id: 'vat-rates',
          question: 'How does VAT calculation work?',
          answer: `The system automatically applies correct UK VAT rates: 20% standard rate, 5% reduced rate, 
          and 0% zero rate. You can configure default rates for different product categories, and the system 
          will suggest the appropriate rate based on item descriptions. All calculations are HMRC compliant.`
        },
        {
          id: 'companies-house',
          question: 'Does it integrate with Companies House?',
          answer: `Yes, we integrate with Companies House to automatically validate company numbers, retrieve 
          company information, and track filing deadlines. The system will remind you of upcoming confirmation 
          statements and annual returns to ensure you never miss a deadline.`
        },
        {
          id: 'vat-returns',
          question: 'How do I submit VAT returns?',
          answer: `VAT returns are generated automatically from your transactions. Simply review the pre-populated 
          return, make any necessary adjustments, and submit directly to HMRC with one click. The system handles 
          all the technical MTD requirements and provides confirmation receipts.`
        }
      ]
    },
    {
      id: 'banking-integration',
      title: 'Banking & Open Banking',
      icon: <BankIcon color="primary" />,
      faqs: [
        {
          id: 'open-banking-security',
          question: 'Is Open Banking secure?',
          answer: `Yes, Open Banking is extremely secure and regulated by the FCA. We use bank-level encryption 
          and never store your banking credentials. The connection is read-only, so we can import transactions 
          but cannot make payments or transfers. You can disconnect at any time.`
        },
        {
          id: 'supported-banks',
          question: 'Which UK banks are supported?',
          answer: `We support all major UK banks including Barclays, HSBC, Lloyds, NatWest, Santander, Nationwide, 
          TSB, and most building societies. We also support business banking from Starling, Monzo, Revolut, 
          and other digital banks.`
        },
        {
          id: 'bank-reconciliation',
          question: 'How does bank reconciliation work?',
          answer: `Bank reconciliation is largely automated. Imported transactions are automatically matched 
          with invoices, bills, and expenses using AI. You simply review and confirm matches, with most 
          reconciliation happening automatically. This saves hours compared to manual reconciliation.`
        },
        {
          id: 'multiple-accounts',
          question: 'Can I connect multiple bank accounts?',
          answer: `Yes, you can connect multiple business bank accounts, credit cards, and even personal accounts 
          if used for business. Each account appears separately in your dashboard with consolidated reporting 
          across all accounts.`
        }
      ]
    },
    {
      id: 'pricing-billing',
      title: 'Pricing & Billing',
      icon: <BusinessIcon color="primary" />,
      faqs: [
        {
          id: 'pricing-plans',
          question: 'What are your pricing plans?',
          answer: `We offer transparent, competitive pricing: Starter £19/month (perfect for sole traders), 
          Professional £39/month (most popular for small businesses), Business £69/month (growing companies), 
          and Enterprise £99/month (includes cross-domain integration). All plans include unlimited users 
          and UK support.`
        },
        {
          id: 'free-trial',
          question: 'Do you offer a free trial?',
          answer: `Yes! We offer a 30-day free trial with full access to all features. No credit card required 
          to start. You can import your data, connect your bank, and create invoices during the trial to 
          see how much time and money you'll save.`
        },
        {
          id: 'cancel-anytime',
          question: 'Can I cancel anytime?',
          answer: `Absolutely. There are no long-term contracts or cancellation fees. You can cancel anytime 
          and your data remains accessible for 12 months. We also provide data export in multiple formats 
          if you decide to switch to another system.`
        },
        {
          id: 'what-included',
          question: 'What\'s included in each plan?',
          answer: `All plans include: unlimited invoices and expenses, VAT returns, bank reconciliation, 
          financial reporting, and UK support. Higher plans add features like multi-currency, advanced 
          reporting, API access, and our unique cross-domain business intelligence integration.`
        }
      ]
    },
    {
      id: 'security-data',
      title: 'Security & Data',
      icon: <SecurityIcon color="primary" />,
      faqs: [
        {
          id: 'data-security',
          question: 'How secure is my financial data?',
          answer: `Your data is protected with bank-level security including 256-bit SSL encryption, 
          two-factor authentication, and regular security audits. All data is stored in UK data centres 
          and we're fully GDPR compliant. We never share your data with third parties.`
        },
        {
          id: 'data-backup',
          question: 'Is my data backed up?',
          answer: `Yes, your data is automatically backed up multiple times daily to secure UK data centres. 
          We maintain 99.9% uptime and can restore your data from any point in the last 30 days if needed. 
          You can also export your data anytime for your own backup.`
        },
        {
          id: 'gdpr-compliance',
          question: 'Are you GDPR compliant?',
          answer: `Yes, we're fully GDPR compliant. You control your data and can request exports, corrections, 
          or deletion at any time. We have a UK-based Data Protection Officer and follow all GDPR requirements 
          for data processing and storage.`
        },
        {
          id: 'data-ownership',
          question: 'Who owns my data?',
          answer: `You own your data completely. We're simply the custodian. You can export your data in 
          multiple formats anytime, and if you cancel, your data remains accessible for 12 months before 
          being securely deleted (unless you request earlier deletion).`
        }
      ]
    },
    {
      id: 'support',
      title: 'Support & Training',
      icon: <SupportIcon color="primary" />,
      faqs: [
        {
          id: 'support-hours',
          question: 'What are your support hours?',
          answer: `Our UK-based support team is available Monday-Friday 9am-6pm GMT via live chat, email, 
          and phone. We also have extensive self-help resources including video tutorials, user guides, 
          and this FAQ section available 24/7.`
        },
        {
          id: 'training-available',
          question: 'Do you provide training?',
          answer: `Yes! We offer free onboarding sessions for all new customers, plus ongoing training webinars. 
          Our video tutorial library covers every feature, and we can provide custom training sessions for 
          larger teams. Most users find the system intuitive enough to use immediately.`
        },
        {
          id: 'migration-help',
          question: 'Will you help migrate from my current system?',
          answer: `Absolutely! Our migration team provides free data migration from Sage, Xero, QuickBooks, 
          and other systems. We'll transfer your customers, suppliers, chart of accounts, and historical 
          transactions. Most migrations are completed within 48 hours.`
        },
        {
          id: 'accountant-access',
          question: 'Can my accountant access the system?',
          answer: `Yes, you can give your accountant read-only or full access to your account. Many UK 
          accountants already use our system and appreciate the clean interface and comprehensive reporting. 
          We can also export data in formats your accountant prefers.`
        }
      ]
    }
  ];

  const filteredFAQs = faqCategories.map(category => ({
    ...category,
    faqs: category.faqs.filter(faq => 
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.faqs.length > 0);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <HelpIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
          Frequently Asked Questions
        </Typography>
        <Typography variant="h6" color="text.secondary" paragraph>
          Get answers to common questions about EF-CapFlow
        </Typography>
        <Chip 
          icon={<UKFlagIcon />}
          label="UK-Focused Support" 
          color="secondary" 
          sx={{ mt: 1 }}
        />
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <TextField
          fullWidth
          placeholder="Search FAQs..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h4" color="primary">30s</Typography>
            <Typography variant="body2">Average response time</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h4" color="success.main">99.9%</Typography>
            <Typography variant="body2">Customer satisfaction</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h4" color="info.main">24/7</Typography>
            <Typography variant="body2">Self-help resources</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h4" color="warning.main">🇬🇧</Typography>
            <Typography variant="body2">UK-based support</Typography>
          </Card>
        </Grid>
      </Grid>

      {/* FAQ Categories */}
      {filteredFAQs.map((category) => (
        <Box key={category.id} sx={{ mb: 4 }}>
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
          >
            {category.icon}
            {category.title}
          </Typography>
          
          {category.faqs.map((faq) => (
            <Accordion
              key={faq.id}
              expanded={expandedFAQ === faq.id}
              onChange={handleAccordionChange(faq.id)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ))}

      {/* No Results */}
      {searchTerm && filteredFAQs.length === 0 && (
        <Alert severity="info" sx={{ mt: 4 }}>
          <Typography variant="subtitle1" gutterBottom>
            No FAQs found for "{searchTerm}"
          </Typography>
          <Typography variant="body2">
            Try a different search term or contact our support team for help.
          </Typography>
        </Alert>
      )}

      {/* Contact Support */}
      <Paper sx={{ p: 4, mt: 6, textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
        <Typography variant="h5" gutterBottom>
          Still need help?
        </Typography>
        <Typography variant="body1" paragraph>
          Our UK-based support team is here to help you succeed with EF-CapFlow.
        </Typography>
        
        <Grid container spacing={2} justifyContent="center" sx={{ mt: 3 }}>
          <Grid item>
            <Button 
              variant="outlined" 
              size="large"
              sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: 'white' } }}
            >
              Live Chat
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              size="large"
              sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: 'white' } }}
            >
              Email Support
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              size="large"
              sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: 'white' } }}
            >
              Schedule Call
            </Button>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4 }}>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            💬 Average response time: 30 seconds | 📞 UK support hours: Mon-Fri 9am-6pm GMT
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default FAQPage;
